// src/components/CustomerMenu.js
import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { useReactToPrint } from 'react-to-print';

const url = process.env.REACT_APP_SERVER_URL || "https://food.stall-centralsoft.com";
const stallID = localStorage.getItem("stallID");
const username = localStorage.getItem("username");

export default function CustomerMenu() {
  const [foods, setFoods] = useState([]);
  const [categories, setCategories] = useState([]);
  const [cart, setCart] = useState([]);
  const [tableNumber, setTableNumber] = useState("");
  const [availableAddons, setAvailableAddons] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [loading, setLoading] = useState(true);
  const [showOrderConfirmation, setShowOrderConfirmation] = useState(false);
  const [currentOrder, setCurrentOrder] = useState(null);

  const printRef = useRef();

  // Simple placeholder image
  const PLACEHOLDER = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='150' viewBox='0 0 200 150'%3E%3Crect width='200' height='150' fill='%23f8f9fa'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='Arial' font-size='14' fill='%236c757d'%3ENo Image%3C/text%3E%3C/svg%3E";

  // Clean image URL function
  const getImageUrl = (imagePath) => {
    if (!imagePath || imagePath === "null" || imagePath === "undefined") {
      return PLACEHOLDER;
    }

    // If it's already a full URL, return it
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath;
    }

    // If it's a data URL, return it
    if (imagePath.startsWith('data:')) {
      return imagePath;
    }

    // Clean the path
    let cleanPath = imagePath.trim();
    
    // Remove leading slash if present
    if (cleanPath.startsWith('/')) {
      cleanPath = cleanPath.substring(1);
    }
    
    // Ensure it starts with 'uploads/'
    if (!cleanPath.startsWith('uploads/')) {
      cleanPath = 'uploads/' + cleanPath;
    }
    
    // Return full URL
    return `${url}/${cleanPath}`;
  };

  // Safe numeric conversion
  const getSafePrice = (p) => {
    if (p === null || p === undefined || p === "") return 0;
    const n = Number(p);
    return Number.isFinite(n) ? n : 0;
  };

  // Safe toFixed function
  const safeToFixed = (value, decimals = 2) => {
    const num = getSafePrice(value);
    return num.toFixed(decimals);
  };

  // Print handler
  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    pageStyle: `
      @media print {
        body {
          font-family: 'Courier New', monospace;
          font-size: 12px;
          margin: 0;
          padding: 5px;
        }
        .no-print { display: none !important; }
        .print-only { display: block !important; }
        .kitchen-ticket {
          width: 80mm;
          border: 1px solid #000;
          padding: 8px;
          margin: 0 auto;
          background: white;
        }
        * {
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
      }
      @media screen {
        .print-only { display: none !important; }
      }
    `,
    documentTitle: `Order-${currentOrder?.orderId || ''}`
  });

  // Fetch foods and addons
  useEffect(() => {
    const fetchAll = async () => {
      try {
        setLoading(true);
        
        // Fetch foods
        const foodsRes = await axios.get(`${url}/api/admin/foods`, {
          params: { stallID }
        });

        const foodsData = foodsRes.data.foods || [];
        
        // Process foods
        const processedFoods = foodsData.map((food) => {
          const basePrice = getSafePrice(food.prom_price) > 0 
            ? getSafePrice(food.prom_price) 
            : getSafePrice(food.price);
            
          return {
            id: String(food.id),
            foodName: food.foodName || "Unnamed Food",
            description: food.description || "",
            price: getSafePrice(food.price),
            prom_price: getSafePrice(food.prom_price),
            basePrice,
            imageURL: food.imageURL || "",
            category: food.category || "Uncategorized",
          };
        });

        setFoods(processedFoods);

        // Extract unique categories
        const uniqueCategories = [...new Set(processedFoods.map(f => f.category))];
        setCategories(["All", ...uniqueCategories]);

        // Fetch addons
        const addonsRes = await axios.get(`${url}/api/admin/addons`, {
          params: { stallID }
        });

        const addonsData = addonsRes.data.addons || [];
        
        const processedAddons = addonsData.map(addon => ({
          id: String(addon.id),
          name: addon.name || "Addon",
          price: getSafePrice(addon.price),
          category: addon.category || "extra",
        }));

        setAvailableAddons(processedAddons);

      } catch (err) {
        console.error("Error fetching menu:", err);
        alert("Failed to load menu. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    if (stallID) {
      fetchAll();
    } else {
      alert("Please login to access the menu.");
      setLoading(false);
    }
  }, []);

  // Add to cart function
  const addToCart = useCallback((food) => {
    setCart((prev) => {
      const existingItem = prev.find(item => String(item.id) === String(food.id));
      
      if (existingItem) {
        return prev.map(item =>
          String(item.id) === String(food.id)
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }

      const newItem = {
        id: String(food.id),
        foodName: food.foodName,
        basePrice: food.basePrice,
        price: food.price,
        prom_price: food.prom_price,
        quantity: 1,
        selectedAddons: [],
        addonsTotal: 0,
        finalPrice: food.basePrice,
        notes: "",
      };
      
      return [...prev, newItem];
    });
  }, []);

  // Update item quantity
  const updateItemQuantity = useCallback((itemId, change) => {
    setCart((prev) =>
      prev
        .map((item) =>
          String(item.id) === String(itemId)
            ? { ...item, quantity: Math.max(1, item.quantity + change) }
            : item
        )
        .filter(item => item.quantity > 0)
    );
  }, []);

  // Remove item completely
  const removeItem = useCallback((itemId) => {
    setCart((prev) => prev.filter(item => String(item.id) !== String(itemId)));
  }, []);

  // Handle addon selection
  const handleAddonDropdown = useCallback((itemId, addonId) => {
    if (!addonId) return;

    setCart((prev) =>
      prev.map((item) => {
        if (String(item.id) !== String(itemId)) return item;

        const addon = availableAddons.find(a => String(a.id) === String(addonId));
        if (!addon) return item;

        const existingAddonIndex = item.selectedAddons.findIndex(a => String(a.id) === String(addonId));
        let updatedAddons = [...item.selectedAddons];

        if (existingAddonIndex >= 0) {
          updatedAddons[existingAddonIndex] = {
            ...updatedAddons[existingAddonIndex],
            quantity: updatedAddons[existingAddonIndex].quantity + 1,
          };
        } else {
          updatedAddons.push({ ...addon, quantity: 1 });
        }

        const addonsTotal = updatedAddons.reduce((sum, a) => sum + (a.price * a.quantity), 0);

        return {
          ...item,
          selectedAddons: updatedAddons,
          addonsTotal,
          finalPrice: item.basePrice + addonsTotal,
        };
      })
    );
  }, [availableAddons]);

  // Update addon quantity
  const updateAddonQuantity = useCallback((itemId, addonId, change) => {
    setCart((prev) =>
      prev.map((item) => {
        if (String(item.id) !== String(itemId)) return item;

        let updatedAddons = item.selectedAddons.map((a) =>
          String(a.id) === String(addonId)
            ? { ...a, quantity: Math.max(0, a.quantity + change) }
            : a
        );

        updatedAddons = updatedAddons.filter(a => a.quantity > 0);

        const addonsTotal = updatedAddons.reduce((sum, a) => sum + (a.price * a.quantity), 0);

        return {
          ...item,
          selectedAddons: updatedAddons,
          addonsTotal,
          finalPrice: item.basePrice + addonsTotal,
        };
      })
    );
  }, []);

  // Update notes
  const updateNotes = useCallback((itemId, notes) => {
    setCart((prev) =>
      prev.map((item) =>
        String(item.id) === String(itemId) ? { ...item, notes } : item
      )
    );
  }, []);

  // Calculate item total
  const calculateItemTotal = useCallback((item) => {
    return item.finalPrice * item.quantity;
  }, []);

  // Cart total
  const cartTotal = cart.reduce((sum, item) => sum + calculateItemTotal(item), 0);

  // Submit order
  const submitOrder = async () => {
    if (!tableNumber.trim()) {
      alert("Please enter table number");
      return;
    }
    if (cart.length === 0) {
      alert("Cart is empty");
      return;
    }

    try {
      const orderData = {
        stallID: stallID,
        tableNumber: tableNumber.trim(),
        total: cartTotal,
        status: "pending",
      };

      const orderRes = await axios.post(`${url}/api/admin/orders`, orderData);

      if (orderRes.data.success) {
        const orderId = orderRes.data.orderId;

        // Prepare and send order items
        for (const item of cart) {
          const addonsText = item.selectedAddons.length > 0
            ? `Add-ons: ${item.selectedAddons.map(a => `${a.quantity} × ${a.name}`).join(", ")}`
            : "";

          const notes = item.notes 
            ? (addonsText ? `${item.notes} | ${addonsText}` : item.notes)
            : addonsText;

          const itemData = {
            stallID,
            orderId,
            foodId: item.id,
            foodName: item.foodName,
            quantity: item.quantity,
            unitPrice: item.finalPrice,
            notes,
          };

          await axios.post(`${url}/api/admin/order-items`, itemData);
        }

        // Set current order for confirmation
        setCurrentOrder({
          orderId,
          stallID,
          tableNumber,
          totalAmount: cartTotal,
          orderTime: new Date().toLocaleString('en-MY')
        });

        setShowOrderConfirmation(true);
        setCart([]);
        setTableNumber("");

      } else {
        alert("Failed to create order");
      }
    } catch (err) {
      console.error("Order error:", err);
      alert("Error placing order. Please try again.");
    }
  };

  // Back to menu function
  const handleBackToMenu = () => {
    setShowOrderConfirmation(false);
    setCurrentOrder(null);
  };

  // Filter foods by category
  const filteredFoods = selectedCategory === "All"
    ? foods
    : foods.filter(food => food.category === selectedCategory);

  // Order Confirmation Component
  const OrderConfirmation = ({ order, onBack, onPrint, printRef }) => {
    return (
      <div className="container-fluid">
        <div className="no-print mb-4">
          <button className="btn btn-secondary me-2" onClick={onBack}>
            ← Back to Menu
          </button>
          <button className="btn btn-primary" onClick={onPrint}>
            🖨️ Print Receipt
          </button>
        </div>

        {/* Printable Receipt */}
        <div ref={printRef} className="kitchen-ticket">
          <div style={{ textAlign: "center", marginBottom: "10px", borderBottom: "1px solid #000", paddingBottom: "8px" }}>
            <h2 style={{ margin: "0", fontSize: "16px", fontWeight: "bold" }}>KITCHEN ORDER</h2>
            <p style={{ margin: "2px 0", fontSize: "10px" }}>Receipt</p>
          </div>

          <div style={{ marginBottom: "8px", borderBottom: "1px dashed #000", paddingBottom: "8px" }}>
            <p style={{ margin: "2px 0", fontSize: "10px" }}><strong>Order #:</strong> {order.orderId}</p>
            <p style={{ margin: "2px 0", fontSize: "10px" }}><strong>Table #:</strong> {order.tableNumber}</p>
            <p style={{ margin: "2px 0", fontSize: "10px" }}><strong>Time:</strong> {order.orderTime}</p>
          </div>

          <div style={{ marginBottom: "8px" }}>
            <p style={{ margin: "2px 0", fontSize: "10px", fontWeight: "bold" }}>Order Placed Successfully!</p>
          </div>

          <div style={{ borderTop: "2px solid #000", paddingTop: "8px", textAlign: "center" }}>
            <p style={{ margin: "2px 0", fontSize: "11px", fontWeight: "bold" }}>
              TOTAL: RM {safeToFixed(order.totalAmount)}
            </p>
          </div>

          <div style={{ textAlign: "center", marginTop: "10px", fontSize: "8px" }}>
            <p>*** KITCHEN COPY ***</p>
            <p>Thank you!</p>
          </div>
        </div>

        {/* Screen Confirmation */}
        <div className="no-print mt-4">
          <div className="alert alert-success">
            <h4 className="alert-heading">✅ Order Placed Successfully!</h4>
            <p><strong>Order ID:</strong> {order.orderId}</p>
            <p><strong>Table Number:</strong> {order.tableNumber}</p>
            <p><strong>Total Amount:</strong> RM {safeToFixed(order.totalAmount)}</p>
            <p><strong>Order Time:</strong> {order.orderTime}</p>
          </div>
        </div>
      </div>
    );
  };

  // Loading state
  if (loading) {
    return (
      <div className="container-fluid">
        <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <span className="ms-2">Loading menu...</span>
        </div>
      </div>
    );
  }

  // Order confirmation view
  if (showOrderConfirmation && currentOrder) {
    return (
      <OrderConfirmation
        order={currentOrder}
        onBack={handleBackToMenu}
        onPrint={handlePrint}
        printRef={printRef}
      />
    );
  }

  // Main menu view
  return (
    <div className="container-fluid">
      <div className="bg-primary text-white p-3 mb-4">
        <h1 className="text-center">🍽️ Food Ordering</h1>
        <p className="text-center mb-0">
          Stall: <strong>{username}</strong> | ID: <strong>{stallID}</strong>
        </p>
      </div>

      <div className="row">
        {/* Menu Section */}
        <div className="col-md-8">
          <div className="mb-3">
            <div className="btn-group flex-wrap" role="group">
              <button
                className={`btn btn-sm ${selectedCategory === "All" ? "btn-primary" : "btn-outline-primary"}`}
                onClick={() => setSelectedCategory("All")}
              >
                All
              </button>
              {categories.filter(c => c !== "All").map((category) => (
                <button
                  key={category}
                  className={`btn btn-sm ${selectedCategory === category ? "btn-primary" : "btn-outline-primary"}`}
                  onClick={() => setSelectedCategory(category)}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>

          <div className="row">
            {filteredFoods.length > 0 ? (
              filteredFoods.map((food) => {
                const imageUrl = getImageUrl(food.imageURL);
                
                return (
                  <div key={food.id} className="col-md-6 col-lg-4 mb-4">
                    <div className="card h-100 shadow-sm">
                      {/* Food Image */}
                      <div 
                        className="card-img-top" 
                        style={{ 
                          height: '180px', 
                          overflow: 'hidden',
                          backgroundColor: '#f8f9fa'
                        }}
                      >
                        <img
                          src={imageUrl}
                          alt={food.foodName}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = PLACEHOLDER;
                          }}
                        />
                      </div>

                      <div className="card-body d-flex flex-column">
                        <h5 className="card-title">{food.foodName}</h5>
                        <p className="card-text flex-grow-1 text-muted small">
                          {food.description}
                        </p>

                        <div className="d-flex justify-content-between align-items-center mt-2">
                          <div>
                            {food.prom_price > 0 ? (
                              <>
                                <strong className="text-danger">RM {safeToFixed(food.prom_price)}</strong>
                                <small className="text-muted text-decoration-line-through ms-2">
                                  RM {safeToFixed(food.price)}
                                </small>
                              </>
                            ) : (
                              <strong>RM {safeToFixed(food.price)}</strong>
                            )}
                          </div>
                          <button
                            className="btn btn-success btn-sm"
                            onClick={() => addToCart(food)}
                          >
                            + Add
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="col-12">
                <div className="alert alert-info">
                  <p className="mb-0">No food items available in this category.</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Cart Section */}
        <div className="col-md-4">
          <div className="card shadow sticky-top" style={{ top: '20px' }}>
            <div className="card-header bg-warning">
              <h5 className="mb-0">🛒 Your Cart ({cart.length})</h5>
            </div>
            <div className="card-body">
              <div className="mb-3">
                <label className="form-label">Table Number *</label>
                <input
                  type="text"
                  className="form-control"
                  value={tableNumber}
                  onChange={(e) => setTableNumber(e.target.value)}
                  placeholder="e.g., T01"
                  required
                />
              </div>

              {cart.length === 0 ? (
                <p className="text-muted text-center">Cart is empty</p>
              ) : (
                <div className="cart-items mb-3" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  {cart.map((item) => (
                    <div key={item.id} className="border-bottom pb-3 mb-3">
                      <div className="d-flex justify-content-between align-items-start">
                        <div className="flex-grow-1">
                          <h6 className="mb-1">{item.foodName}</h6>
                          
                          {/* Quantity Controls */}
                          <div className="d-flex align-items-center mb-2">
                            <button
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() => updateItemQuantity(item.id, -1)}
                            >
                              −
                            </button>
                            <span className="mx-2">{item.quantity}</span>
                            <button
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() => updateItemQuantity(item.id, 1)}
                            >
                              +
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger ms-3"
                              onClick={() => removeItem(item.id)}
                            >
                              Remove
                            </button>
                          </div>

                          {/* Addons */}
                          <div className="mb-2">
                            <select
                              className="form-select form-select-sm"
                              onChange={(e) => {
                                handleAddonDropdown(item.id, e.target.value);
                                e.target.value = "";
                              }}
                            >
                              <option value="">Add add-on...</option>
                              {availableAddons.map((addon) => (
                                <option key={addon.id} value={addon.id}>
                                  {addon.name} (+RM {safeToFixed(addon.price)})
                                </option>
                              ))}
                            </select>
                          </div>

                          {/* Selected Addons */}
                          {item.selectedAddons.length > 0 && (
                            <div className="bg-light p-2 rounded mb-2">
                              {item.selectedAddons.map((addon) => (
                                <div key={addon.id} className="d-flex justify-content-between align-items-center mb-1">
                                  <small>{addon.name} (x{addon.quantity})</small>
                                  <div className="d-flex align-items-center">
                                    <button
                                      className="btn btn-xs btn-outline-secondary btn-sm p-0"
                                      style={{ width: '20px', height: '20px' }}
                                      onClick={() => updateAddonQuantity(item.id, addon.id, -1)}
                                    >
                                      −
                                    </button>
                                    <button
                                      className="btn btn-xs btn-outline-secondary btn-sm p-0 ms-1"
                                      style={{ width: '20px', height: '20px' }}
                                      onClick={() => updateAddonQuantity(item.id, addon.id, 1)}
                                    >
                                      +
                                    </button>
                                    <small className="ms-2">RM {safeToFixed(addon.price * addon.quantity)}</small>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Notes */}
                          <div className="mb-2">
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              placeholder="Special instructions..."
                              value={item.notes}
                              onChange={(e) => updateNotes(item.id, e.target.value)}
                            />
                          </div>

                          {/* Item Total */}
                          <div className="d-flex justify-content-between">
                            <small>Item Total:</small>
                            <strong>RM {safeToFixed(calculateItemTotal(item))}</strong>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="border-top pt-3">
                <div className="d-flex justify-content-between fw-bold fs-5 mb-3">
                  <span>Total:</span>
                  <span>RM {safeToFixed(cartTotal)}</span>
                </div>
                <button
                  className="btn btn-success w-100"
                  onClick={submitOrder}
                  disabled={!tableNumber.trim() || cart.length === 0}
                >
                  📋 Place Order
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}